import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Logo from '../../../common/Logo';
import Facebook from '../../../Helpers/icons/Facebook';
import Instagram from '../../../Helpers/icons/Instagram';
import LinkedIn from '../../../Helpers/icons/LinkedIn';
import { Globe, Mail } from '../../../Helpers/icons/Icons';

const FooterHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className='mb-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-300'>
    {children}
  </h2>
);

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className='text-[14px] text-ink-200 transition-colors hover:text-apricot-300'>
    {children}
  </Link>
);

export default function Footer() {
  const { t } = useTranslation();

  const social = [
    { href: process.env.REACT_APP_INSTAGRAM_URL, label: 'Instagram', Icon: Instagram },
    { href: process.env.REACT_APP_FACEBOOK_URL, label: 'Facebook', Icon: Facebook },
    { href: process.env.REACT_APP_LINKEDIN_URL, label: 'LinkedIn', Icon: LinkedIn },
  ];

  return (
    <footer className='band-ink print:hidden'>
      <div className='container-x'>
        <div className='grid gap-10 py-14 md:grid-cols-12 md:py-16'>
          <div className='md:col-span-5 lg:col-span-4'>
            <Logo size={44} onDark />
            <p className='mt-5 max-w-[34ch] text-[14px] leading-relaxed text-ink-300'>
              {t('about_us_description.title')}
            </p>
            <div className='mt-6 flex items-center gap-2'>
              {social.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target='_blank'
                  rel='noreferrer'
                  aria-label={label}
                  className='flex h-10 w-10 items-center justify-center rounded-pill border border-ink-700 text-ink-200 transition-colors hover:border-apricot-500 hover:bg-apricot-500 hover:text-ink-900'
                >
                  <Icon size='18' className='fill-current' />
                </a>
              ))}
            </div>
          </div>

          <div className='md:col-span-3 lg:col-span-2 lg:col-start-6'>
            <FooterHeading>{t('gift_cards')}</FooterHeading>
            <nav className='flex flex-col gap-3'>
              <FooterLink to='/products'>{t('gift_cards')}</FooterLink>
              <FooterLink to='/companies'>{t('companies')}</FooterLink>
              <FooterLink to='/blog'>{t('blog')}</FooterLink>
              <FooterLink to='/about'>{t('about_us')}</FooterLink>
            </nav>
          </div>

          <div className='md:col-span-4 lg:col-span-2'>
            <FooterHeading>{t('partnership')}</FooterHeading>
            <nav className='flex flex-col gap-3'>
              <FooterLink to='/become-seller'>{t('become_a_partner')}</FooterLink>
              <FooterLink to='/request-company'>{t('request_company')}</FooterLink>
              <FooterLink to='/privacy-policy'>{t('privacy_policy')}</FooterLink>
              <FooterLink to='/terms-of-services'>{t('terms_of_service')}</FooterLink>
            </nav>
          </div>

          <div className='md:col-span-5 lg:col-span-3 lg:col-start-10'>
            <FooterHeading>{t('contact_us')}</FooterHeading>
            <ul className='flex flex-col gap-3 text-[14px] text-ink-200'>
              <li>
                <a
                  href='mailto:info@egiftcards.am'
                  className='flex items-center gap-2.5 transition-colors hover:text-apricot-300'
                >
                  <Mail size={15} className='flex-none text-ink-400' />
                  info@egiftcards.am
                </a>
              </li>
              <li className='flex items-start gap-2.5'>
                <Globe size={15} className='mt-0.5 flex-none text-ink-400' />
                <span>{t('online_business')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className='flex flex-col items-start justify-between gap-3 border-t border-ink-800 py-6 text-[12px] text-ink-400 sm:flex-row sm:items-center'>
          <span>
            © {new Date().getFullYear()} {t('online_gift_cards')}
          </span>
          <div className='flex items-center gap-5'>
            <Link to='/privacy-policy' className='transition-colors hover:text-ink-200'>
              {t('privacy_policy')}
            </Link>
            <Link to='/terms-of-services' className='transition-colors hover:text-ink-200'>
              {t('terms_of_service')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
